import React, { useState } from 'react';
import {
  Box,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  VStack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';

export type NumericInputType = {
  maxValue: number;
  minValue: number;
};

interface NumericInputProps {
  isDisabled?: boolean;
  value: NumericInputType;
  onChange: (numericDefinition: NumericInputType) => void;
}

// BE refuses values lower or higher then these, so we must enforce them on front-end
const BE_LIMITS = {
  min: Math.pow(-2, 31),
  max: Math.pow(2, 31) - 1,
};

export const NumericInput = ({
  value,
  onChange,
  isDisabled,
}: NumericInputProps) => {
  const [error, setError] = useState('');

  return (
    <VStack justifyContent="flex-start">
      <HStack>
        <Text fontSize="sm" fontStyle="italic" p={2}>
          {t`Numeric answer between`}
        </Text>
        <NumberInput
          size="sm"
          maxW={24}
          precision={0}
          isDisabled={isDisabled}
          {...BE_LIMITS}
          clampValueOnBlur
          defaultValue={value.minValue}
          onChange={(v, newMinValue) => {
            if (newMinValue > value.maxValue) {
              setError(t`Minimum value must be lower than max value`);
            } else if (error) {
              setError('');
            }

            onChange({
              minValue: Math.max(BE_LIMITS.min, Math.round(newMinValue)),
              maxValue: Math.round(value.maxValue),
            });
          }}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text fontSize="sm" fontStyle="italic" p={2}>
          {t`and`}
        </Text>
        <NumberInput
          size="sm"
          maxW={24}
          precision={0}
          isDisabled={isDisabled}
          {...BE_LIMITS}
          clampValueOnBlur
          defaultValue={value.maxValue}
          onChange={(v, newMaxValue) => {
            if (newMaxValue < value.minValue) {
              setError(t`Minimum value must be lower than max value`);
            } else if (error) {
              setError('');
            }

            onChange({
              minValue: value.minValue,
              maxValue: Math.min(BE_LIMITS.max, Math.round(newMaxValue)),
            });
          }}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </HStack>
      {error ? <Box color="red.500">{error}</Box> : null}
    </VStack>
  );
};
